"use client";

import { findProfile, PROFILES, resolveOpinions, ZMANIM } from "@kedaat/definitions";
import {
  formatPlainDate,
  formatTime,
  israelDateOf,
  ZmanimEvaluator,
  type ZmanId,
} from "@kedaat/engine";
import { findLocality, LOCALITIES } from "@kedaat/locations";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useEngine } from "@/lib/hooks";
import {
  formatYearMonth,
  gregorianRange,
  narrowWeekday,
  parseYearMonth,
  weekdayIndex,
  type YearMonth,
} from "@/lib/month";
import { settingsToQuery } from "@/lib/settings";
import { useSettings } from "@/lib/useSettings";
import { computeDay, pointFor, type DayResult } from "@/lib/zmanim";
import { LocalityPicker } from "./LocalityPicker";
import { ProfilePicker } from "./ProfilePicker";

const COLUMN_CHOICES = ZMANIM.filter((z) => z.group !== "pesach");
const DEFAULT_COLUMNS: ZmanId[] = [
  "alos",
  "misheyakir",
  "sunrise",
  "sof_zman_shma",
  "sof_zman_tfila",
  "chatzot",
  "mincha_gedola",
  "plag_hamincha",
  "sunset",
  "tzeit",
];
const COLUMNS_KEY = "kedaat.luach.columns.v1";

const button =
  "rounded-md border border-line bg-card px-3 py-2 text-sm font-medium hover:bg-accent-soft";

function notesFor({ info, special }: DayResult): string {
  const notes: string[] = [];
  if (info.holiday) notes.push(info.holiday);
  if (info.parsha) {
    notes.push(`פרשת ${info.parsha}${info.specialShabbat ? ` (שבת ${info.specialShabbat})` : ""}`);
  }
  if (info.roshChodesh && !info.holiday) notes.push(info.roshChodesh);
  if (special.fast) {
    const { start, end } = special.fast;
    notes.push(`צום ${formatTime(start.ms, start.rounding)}–${formatTime(end.ms, end.rounding)}`);
  }
  if (special.candleLighting) {
    const { ms, rounding } = special.candleLighting;
    notes.push(`נרות ${formatTime(ms, rounding)}`);
  }
  if (special.shabbatEnd) {
    const { time, rabbeinuTam } = special.shabbatEnd;
    notes.push(
      `צאת ${formatTime(time.ms, time.rounding)} · ר״ת ${formatTime(rabbeinuTam.ms, rabbeinuTam.rounding)}`,
    );
  }
  return notes.join(" · ");
}

/** A printable monthly table of zmanim for the chosen locality and opinions. */
export function MonthLuach() {
  const engine = useEngine();
  const { settings, update, ready } = useSettings();
  const [month, setMonth] = useState<YearMonth | null>(null);
  const [columns, setColumns] = useState<ZmanId[]>(DEFAULT_COLUMNS);
  const [today, setToday] = useState<string | null>(null);

  useEffect(() => {
    setToday(formatPlainDate(israelDateOf(Date.now())));
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(COLUMNS_KEY) ?? "null");
      if (Array.isArray(saved)) {
        setColumns(COLUMN_CHOICES.map((z) => z.id).filter((id) => saved.includes(id)));
      }
    } catch {
      // Keep the default columns.
    }
  }, []);

  // The Hebrew month of today, or the one named in the address.
  useEffect(() => {
    if (!engine || month) return;
    const fromUrl = parseYearMonth(new URLSearchParams(window.location.search).get("m"));
    setMonth(fromUrl ?? engine.calendar.hebrewMonthOf(israelDateOf(Date.now())));
  }, [engine, month]);

  const locality = findLocality(settings.locality) ?? LOCALITIES[0]!;
  const profile = findProfile(settings.profile) ?? PROFILES[0]!;
  const candleMinutes = settings.candleMinutes[locality.code] ?? locality.candle.minutes;

  useEffect(() => {
    if (!ready || !month) return;
    const query = `${settingsToQuery(settings, null)}&m=${formatYearMonth(month)}`;
    if (window.location.search !== query) window.history.replaceState(null, "", query);
  }, [ready, settings, month]);

  const toggleColumn = (id: ZmanId) => {
    const next = columns.includes(id)
      ? columns.filter((c) => c !== id)
      : COLUMN_CHOICES.map((z) => z.id).filter((z) => z === id || columns.includes(z));
    setColumns(next);
    try {
      localStorage.setItem(COLUMNS_KEY, JSON.stringify(next));
    } catch {
      // Storage may be unavailable; the choice still applies to this visit.
    }
  };

  const days = useMemo(() => {
    if (!engine || !ready || !month) return null;
    const evaluator = new ZmanimEvaluator(engine.astro, pointFor(locality));
    return engine.calendar
      .hebrewMonthDates(month)
      .map((date) =>
        computeDay(evaluator, engine.calendar, date, profile, settings.overrides, candleMinutes),
      );
  }, [engine, ready, month, locality, profile, settings.overrides, candleMinutes]);

  // Open on today, so the month does not have to be scrolled to find it.
  const todayRow = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    todayRow.current?.scrollIntoView({ block: "center", inline: "nearest" });
  }, [days]);

  const opinions = resolveOpinions(profile, settings.overrides);
  const shown = COLUMN_CHOICES.filter((z) => columns.includes(z.id));
  const overrideCount = Object.keys(settings.overrides).length;
  const cell = "px-2 py-2 print:px-1 print:py-0.5";
  /** A thin rule between columns, so the eye can follow a row across the table. */
  const divider = "border-s border-line/70";

  return (
    <div className="space-y-5">
      <div className="space-y-3 print:hidden">
        <h1 className="font-serif text-3xl font-bold">לוח זמנים חודשי</h1>
        <section className="grid gap-3 sm:grid-cols-2">
          <LocalityPicker
            locality={locality}
            onSelect={(l) => update(() => ({ locality: l.code }))}
          />
          <ProfilePicker
            profile={profile}
            overrideCount={overrideCount}
            onSelect={(id) => update(() => ({ profile: id, overrides: {} }))}
            onResetOverrides={() => update(() => ({ overrides: {} }))}
          />
        </section>
        {month && engine && (
          <nav aria-label="בחירת חודש" className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={button}
              onClick={() => setMonth(engine.calendar.addHebrewMonths(month, -1))}
              aria-label="החודש הקודם"
            >
              →
            </button>
            <span className="min-w-32 text-center font-medium">
              {engine.calendar.hebrewMonthTitle(month)}
            </span>
            <button
              type="button"
              className={button}
              onClick={() => setMonth(engine.calendar.addHebrewMonths(month, 1))}
              aria-label="החודש הבא"
            >
              ←
            </button>
            <button
              type="button"
              className={button}
              onClick={() => setMonth(engine.calendar.hebrewMonthOf(israelDateOf(Date.now())))}
            >
              החודש
            </button>
            <button type="button" className={`${button} ms-auto`} onClick={() => window.print()}>
              הדפסה
            </button>
          </nav>
        )}
        <details className="rounded-lg border border-line bg-card px-4 py-3">
          <summary className="cursor-pointer text-sm font-medium">
            עמודות בטבלה ({shown.length})
          </summary>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {COLUMN_CHOICES.map((z) => (
              <label key={z.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={columns.includes(z.id)}
                  onChange={() => toggleColumn(z.id)}
                  className="size-4 accent-[var(--color-accent)]"
                />
                {z.label.he}
              </label>
            ))}
          </div>
        </details>
      </div>

      {days && month && engine ? (
        <article className="space-y-3">
          <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 className="font-serif text-2xl font-bold print:text-lg">
              זמני היום ב{locality.nameHe} · {engine.calendar.hebrewMonthTitle(month)}
            </h2>
            <p className="text-sm text-muted">
              {gregorianRange(days[0]!.info.date, days.at(-1)!.info.date)} · שיטת {profile.label.he}
              {overrideCount > 0 ? " (מותאם)" : ""}
            </p>
          </header>

          <div
            tabIndex={0}
            role="region"
            aria-label="טבלת הזמנים לחודש"
            className="overflow-x-auto rounded-xl border border-line print:overflow-visible print:rounded-none"
          >
            <table className="w-full border-collapse text-sm tabular-nums print:text-[8pt]">
              <caption className="sr-only">
                זמני היום ב{locality.nameHe} לפי שיטת {profile.label.he}. השיטה של כל עמודה מופיעה
                מתחת לטבלה.
              </caption>
              <thead>
                <tr className="sticky top-0 z-20 bg-paper text-xs shadow-[0_1px_0_var(--color-line)] print:static print:text-[7.5pt] print:shadow-none">
                  <th
                    scope="col"
                    className={`${cell} sticky start-0 z-30 bg-paper text-start print:static`}
                  >
                    יום
                  </th>
                  <th scope="col" className={`${cell} ${divider} text-start`}>
                    לועזי
                  </th>
                  {shown.map((z) => (
                    <th
                      key={z.id}
                      scope="col"
                      className={`${cell} ${divider} whitespace-nowrap text-center text-sm font-semibold print:text-[7.5pt]`}
                    >
                      {z.short.he}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {days.map((day, i) => {
                  const { info } = day;
                  // The whole table is one Hebrew month, so the day alone identifies the row.
                  const hebrewDay = info.hebrewDate.split(" ")[0];
                  const iso = formatPlainDate(info.date);
                  const isToday = iso === today;
                  const notes = notesFor(day);
                  // Opaque row colors, so the sticky date column hides what scrolls beneath it.
                  const background = info.isShabbatOrYomTov
                    ? "bg-accent-soft font-medium"
                    : i % 2 === 0
                      ? "bg-card"
                      : "bg-paper";
                  return (
                    <Fragment key={iso}>
                      <tr
                        ref={isToday ? todayRow : undefined}
                        data-date={iso}
                        aria-current={isToday ? "date" : undefined}
                        className={`${background} ${notes ? "" : "border-b border-line/70"} ${
                          weekdayIndex(info.date) === 0 ? "border-t-2 border-t-line" : ""
                        } ${isToday ? "outline-2 -outline-offset-2 outline-accent print:outline-0" : ""}`}
                      >
                        <th
                          scope="row"
                          className={`${cell} sticky start-0 z-10 whitespace-nowrap bg-inherit text-start font-medium print:static`}
                        >
                          {hebrewDay} {narrowWeekday(info.date)}
                        </th>
                        <td className={`${cell} ${divider} whitespace-nowrap text-muted`}>
                          {info.date.day}.{info.date.month}
                        </td>
                        {shown.map((z) => {
                          const row = day.rows.find((r) => r.zman.id === z.id);
                          return (
                            <td key={z.id} className={`${cell} ${divider} text-center`}>
                              {row?.ms == null ? "—" : formatTime(row.ms, row.opinion.rounding)}
                            </td>
                          );
                        })}
                      </tr>
                      {notes && (
                        <tr
                          data-notes-for={iso}
                          className={`${background} border-b border-line/70`}
                        >
                          <td
                            colSpan={2 + shown.length}
                            className="px-2 pb-1.5 text-xs text-accent print:px-1 print:text-[7pt]"
                          >
                            {/* Stays in view when the table is scrolled sideways. */}
                            <span className="sticky start-0 inline-block print:static">
                              {notes}
                            </span>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* The column headings carry only the name of the zman; the shitot are listed here. */}
          <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted print:text-[7pt]">
            {shown.map((z) => (
              <div key={z.id} className="flex gap-1">
                <dt className="font-semibold text-ink">{z.short.he}</dt>
                <dd>{opinions[z.id].label.he}</dd>
              </div>
            ))}
          </dl>

          <footer className="text-xs leading-relaxed text-muted">
            הזמנים מעוגלים לחומרא: סוף זמן כלפי מטה ותחילת זמן כלפי מעלה. הדלקת נרות {candleMinutes}{" "}
            דק׳ לפני השקיעה, צאת שבת 8.5°. השיטות טרם אומתו על ידי רב — אין להסתמך על הזמנים למעשה.
            כדעת · זמני היום בהלכה.
          </footer>
        </article>
      ) : (
        <div
          className="h-96 animate-pulse rounded-lg bg-card"
          aria-busy="true"
          aria-label="מחשב זמנים"
        />
      )}
    </div>
  );
}
